import React, { useEffect, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useQuery, gql } from '@apollo/client'
import { get } from 'lodash'
import qs from 'query-string'
import { withAuthenticatedRoute } from 'lib/auth'
import { GET_LESSON_CREDIT_BALANCE } from './SelectLessonType/query'
import SelectLessonTime from './SelectLessonTime'
import SelectLessonTimeRefresh from './SelectLessonTimeRefresh'
import SelectLessonType from './SelectLessonType'
import SelectLessonTypeRefresh from './SelectLessonTypeRefresh'
import SelectStudentNew from './SelectStudentNew'
import ReviewBookingNew from './ReviewBookingNew'
import ReviewBookingNewRefresh from './ReviewBookingNewRefresh'
import ReviewBookingLessonPacks from './ReviewBookingLessonPacks'
import BuyLessonPacksLayout from './BuyLessonPacksLayout'
import RedeemLessonsCredit from './RedeemLessonsCredit'
import RedeemLessonsCreditRefresh from './RedeemLessonsCreditRefresh'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { checkLessonCredits, checkLessonPack } from './utils'

const GET_LESSON_TYPE_BY_ID = gql`
  query GetLessonTypeById($id: ID) {
    lessonTypes(id: $id) {
      id
      lessonPacks {
        quantity
      }
    }
  }
`

const getCurrentUrl = () => {
  return window.location.pathname + window.location.search
}

const SchedulerStepController = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  const { idOrSlug } = useParams()
  const location = useLocation()
  const {
    lessonTypeId,
    locationId,
    startDateTime,
    studentId,
    isLessonPacks,
    lessonPackId,
    lessonCreditBalanceQuantity,
  } = qs.parse(location.search)
  const currentUrl = getCurrentUrl()

  const [isCoachOfferingRefreshEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_COACH_OFFERING_REFRESH,
  ])

  const { data } = useQuery(GET_LESSON_CREDIT_BALANCE)
  const { data: lessonType } = useQuery(GET_LESSON_TYPE_BY_ID, {
    skip: !lessonTypeId,
    variables: { id: lessonTypeId },
  })
  const isLessonPack = useMemo(() => checkLessonPack(lessonType), [lessonType])
  const lessonCreditBalances = useMemo(
    () => data && get(data, 'currentUser.attributes.lessonCreditBalances'),
    [data],
  )
  const hasLessonCredits = useMemo(
    () =>
      checkLessonCredits({
        lessonTypeId: lessonTypeId,
        lessonCreditBalances: lessonCreditBalances,
      }),
    [lessonCreditBalances, lessonTypeId],
  )

  if (
    lessonTypeId &&
    locationId &&
    startDateTime &&
    studentId &&
    isLessonPack &&
    hasLessonCredits // Step to redeem Lesson Pack credits
  ) {
    const previousUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}&startDateTime=${startDateTime}&lessonCreditBalanceQuantity=${lessonCreditBalanceQuantity}`

    return isCoachOfferingRefreshEnabled ? (
      <RedeemLessonsCreditRefresh
        {...{
          idOrSlug,
          lessonTypeId,
          locationId,
          startDateTime,
          studentId,
          previousUrl,
          lessonCreditBalanceQuantity,
        }}
      />
    ) : (
      <RedeemLessonsCredit
        {...{
          idOrSlug,
          lessonTypeId,
          locationId,
          startDateTime,
          studentId,
          previousUrl,
          lessonCreditBalanceQuantity,
        }}
      />
    )
  }

  if (
    lessonTypeId &&
    locationId &&
    startDateTime &&
    studentId &&
    !isLessonPack &&
    !hasLessonCredits
  ) {
    const previousUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}&startDateTime=${startDateTime}`
    return isCoachOfferingRefreshEnabled ? (
      <ReviewBookingNewRefresh
        {...{
          idOrSlug,
          lessonTypeId,
          locationId,
          startDateTime,
          studentId,
          previousUrl,
        }}
      />
    ) : (
      <ReviewBookingNew
        {...{
          idOrSlug,
          lessonTypeId,
          locationId,
          startDateTime,
          studentId,
          previousUrl,
        }}
      />
    )
  }

  if (
    lessonTypeId &&
    locationId &&
    startDateTime &&
    studentId &&
    isLessonPack &&
    !hasLessonCredits
  ) {
    const previousUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}startDateTime=${startDateTime}`
    return (
      <ReviewBookingLessonPacks
        idOrSlug={idOrSlug}
        lessonTypeId={lessonTypeId}
        locationId={locationId}
        startDateTime={startDateTime}
        studentId={studentId}
        previousUrl={previousUrl}
        isLessonPacks={isLessonPacks}
      />
    )
  }
  if (lessonTypeId && locationId && startDateTime) {
    const previousUrl = lessonCreditBalanceQuantity
      ? `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}&lessonCreditBalanceQuantity=${lessonCreditBalanceQuantity}`
      : `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}`

    return (
      <SelectStudentNew
        currentUrl={currentUrl}
        previousUrl={previousUrl}
        lessonTypeId={lessonTypeId}
        lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
      />
    )
  }
  if (lessonTypeId && locationId && isLessonPacks) {
    const previousUrl = `/coach/${idOrSlug}/schedule`
    return (
      <BuyLessonPacksLayout
        previousUrl={previousUrl}
        lessonTypeId={lessonTypeId}
        idOrSlug={idOrSlug}
        locationId={locationId}
        lessonPackId={lessonPackId}
      />
    )
  }
  if (lessonTypeId && locationId) {
    const previousUrl = `/coach/${idOrSlug}/schedule`
    return isCoachOfferingRefreshEnabled ? (
      <SelectLessonTimeRefresh
        currentUrl={currentUrl}
        previousUrl={previousUrl}
        locationId={locationId}
        lessonTypeId={lessonTypeId}
        lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
      />
    ) : (
      <SelectLessonTime
        currentUrl={currentUrl}
        previousUrl={previousUrl}
        locationId={locationId}
        lessonTypeId={lessonTypeId}
        lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
      />
    )
  }
  if (idOrSlug) {
    return isCoachOfferingRefreshEnabled ? (
      <SelectLessonTypeRefresh idOrSlug={idOrSlug} />
    ) : (
      <SelectLessonType idOrSlug={idOrSlug} />
    )
  }
  return null
}
export default withAuthenticatedRoute(SchedulerStepController)
